/**
 * MissionCreate — create a new mission linked to a drone.
 *
 * Planning modes
 * ──────────────
 *  single    — click map once → right-click for action (original flow).
 *  waypoints — click to add sequential WPs, per-WP altitude + action (ArduPilot-style).
 *
 * Access: /missions/new?drone_id=<droneDbId>
 */
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import MissionMap, { SelectedPoint, WaypointData, MissionAction } from '../components/MissionMap'
import './MissionCreate.css'

type DroneItem = { id: string; drone_id: string; tail_number?: string; status: string }

const MISSION_TYPE_LABELS: Record<string, string> = {
  patrol:     'סיור',
  delivery:   'משלוח',
  mapping:    'מיפוי',
  inspection: 'בדיקה',
  survey:     'סקר',
  zone:       'אזור כיסוי',
  custom:     'מותאם אישית',
}

const ACTION_ICON: Record<string, string> = {
  land: '🛬', takeoff: '🛫', drop_delivery: '📦', scan: '🔍', spray: '💧',
}
const ACTION_LABEL: Record<string, string> = {
  land: 'נחיתה', takeoff: 'המראה', drop_delivery: 'הורדת משלוח', scan: 'סריקה', spray: 'ריסוס',
}

type PlanMode = 'single' | 'waypoints' | 'zone'

export default function MissionCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const preselectedId = searchParams.get('drone_id') ?? ''

  const [name,             setName]             = useState('')
  const [missionType,      setMissionType]      = useState('patrol')
  const [droneId,          setDroneId]          = useState(preselectedId)
  const [planMode,         setPlanMode]         = useState<PlanMode>('single')
  const [selectedPt,       setSelectedPt]       = useState<SelectedPoint | null>(null)
  const [waypoints,        setWaypoints]        = useState<WaypointData[]>([])
  const [zoneType,         setZoneType]         = useState<MissionAction>('scan')
  const [error,            setError]            = useState('')
  const [faceRecognition,  setFaceRecognition]  = useState(false)
  const [weaponDetection,  setWeaponDetection]  = useState(false)
  const [sendAlerts,       setSendAlerts]       = useState(false)

  const { data: drones = [] } = useQuery<DroneItem[]>({
    queryKey: ['drones'],
    queryFn: () => api.get('/drones').then(r => r.data),
  })
  const selectedDrone = drones.find(d => d.id === droneId)

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post('/missions', body),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      navigate(`/missions/${res.data.id}`)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'שגיאה ביצירת המשימה'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!droneId) { setError('יש לבחור רחפן'); return }
    if (!name.trim()) { setError('יש להזין שם משימה'); return }

    if (planMode === 'single') {
      if (!selectedPt)             { setError('יש לסמן נקודת יעד על המפה'); return }
      if (!selectedPt.actionType)  { setError('יש לבחור פעולה (לחץ ימני על הסמן)'); return }
      createMutation.mutate({
        drone_id:    droneId,
        name:        name.trim(),
        type:        missionType,
        waypoints:   [],
        action_type: selectedPt.actionType,
        location_lat: String(selectedPt.lat),
        location_lng: String(selectedPt.lng),
        face_recognition: faceRecognition,
        weapon_detection: weaponDetection,
        send_alerts:      sendAlerts,
      })
    } else if (planMode === 'waypoints') {
      if (waypoints.length < 2) { setError('יש להוסיף לפחות 2 נקודות מסלול'); return }
      createMutation.mutate({
        drone_id:    droneId,
        name:        name.trim(),
        type:        missionType,
        waypoints:   waypoints.map((wp, i) => ({
          latitude:  wp.lat,
          longitude: wp.lng,
          altitude:  wp.altitude ?? null,
          action:    wp.action ?? null,
          index:     i + 1,
        })),
        action_type:  waypoints[0].action ?? null,
        location_lat: String(waypoints[0].lat),
        location_lng: String(waypoints[0].lng),
        face_recognition: faceRecognition,
        weapon_detection: weaponDetection,
        send_alerts:      sendAlerts,
      })
    } else {
      // zone mode
      if (waypoints.length < 3) { setError('יש לסמן לפחות 3 נקודות לאזור הכיסוי'); return }
      createMutation.mutate({
        drone_id:    droneId,
        name:        name.trim(),
        type:        'zone',
        waypoints:   waypoints.map((wp, i) => ({
          latitude:  wp.lat,
          longitude: wp.lng,
          altitude:  wp.altitude ?? null,
          action:    zoneType,
          index:     i + 1,
        })),
        action_type:  zoneType,
        location_lat: String(waypoints[0].lat),
        location_lng: String(waypoints[0].lng),
        face_recognition: faceRecognition,
        weapon_detection: weaponDetection,
        send_alerts:      sendAlerts,
      })
    }
  }

  const mapLabel =
    planMode === 'single'    ? 'לחץ לסימון יעד • לחץ ימני לפעולה' :
    planMode === 'waypoints' ? 'לחץ להוספת נקודות מסלול • לחץ ימני לפעולה + גובה' :
                               'לחץ להגדרת גבולות האזור • 3 נקודות מינימום'

  return (
    <div className="mc-page">
      <div className="mc-header">
        <Link to="/missions" className="mc-back">← רשימת משימות</Link>
        <h1>יצירת משימה חדשה</h1>
      </div>

      <form onSubmit={handleSubmit} className="mc-layout">

        {/* ═══ Left: Map panel ════════════════════════════════════════════════ */}
        <div className="mc-map-panel">

          {/* Planning mode toggle */}
          <div className="mc-mode-toggle">
            <button
              type="button"
              className={`mc-mode-btn ${planMode === 'single' ? 'active' : ''}`}
              onClick={() => { setPlanMode('single'); setWaypoints([]) }}
            >
              📍 נקודה
            </button>
            <button
              type="button"
              className={`mc-mode-btn ${planMode === 'waypoints' ? 'active' : ''}`}
              onClick={() => { setPlanMode('waypoints'); setSelectedPt(null) }}
            >
              🗺️ מסלול
            </button>
            <button
              type="button"
              className={`mc-mode-btn ${planMode === 'zone' ? 'active' : ''}`}
              onClick={() => { setPlanMode('zone'); setSelectedPt(null); setWaypoints([]) }}
            >
              ⬡ אזור
            </button>
          </div>

          <div className="mc-map-label">
            <span>
              {planMode === 'single'    ? '🗺️ נקודת יעד' :
               planMode === 'waypoints' ? '🗺️ תכנון מסלול' :
               '⬡ אזור כיסוי'}
            </span>
            <small>{mapLabel}</small>
          </div>

          <MissionMap
            mode={planMode}
            zoneType={planMode === 'zone' ? zoneType : undefined}
            onLocationSelect={planMode === 'single' ? setSelectedPt : undefined}
            onWaypointsChange={planMode !== 'single' ? setWaypoints : undefined}
          />

          {/* Single-point summary */}
          {planMode === 'single' && selectedPt && (
            <div className="mc-point-summary">
              <div className="mc-point-row">
                <span>מיקום</span>
                <code dir="ltr">{selectedPt.lat.toFixed(6)}, {selectedPt.lng.toFixed(6)}</code>
              </div>
              <div className="mc-point-row">
                <span>פעולה</span>
                <strong className={`mc-action-badge ${selectedPt.actionType ? 'has-action' : ''}`}>
                  {selectedPt.actionType
                    ? `${ACTION_ICON[selectedPt.actionType]} ${ACTION_LABEL[selectedPt.actionType]}`
                    : '— טרם נבחרה (לחץ ימני)'}
                </strong>
              </div>
            </div>
          )}

          {/* Waypoints / Zone vertices list */}
          {(planMode === 'waypoints' || planMode === 'zone') && waypoints.length > 0 && (
            <div className="mc-wp-list">
              <div className="mc-wp-list-header">
                <span>{planMode === 'zone'
                  ? `נקודות גבול אזור (${waypoints.length})`
                  : `נקודות מסלול (${waypoints.length})`}
                </span>
              </div>
              {waypoints.map((wp, idx) => (
                <div key={idx} className="mc-wp-row">
                  <span className="mc-wp-num" dir="ltr">
                    {planMode === 'zone' ? `V${idx + 1}` : `WP${idx + 1}`}
                  </span>
                  <code className="mc-wp-coords" dir="ltr">
                    {wp.lat.toFixed(5)}, {wp.lng.toFixed(5)}
                  </code>
                  {wp.altitude != null
                    ? <span className="mc-wp-alt">↕ {wp.altitude}m</span>
                    : <span className="mc-wp-alt muted">—</span>
                  }
                  {wp.action
                    ? <span className="mc-wp-action">{ACTION_ICON[wp.action]} {ACTION_LABEL[wp.action]}</span>
                    : <span className="mc-wp-action muted">—</span>
                  }
                  <button
                    type="button"
                    className="mc-wp-del"
                    onClick={() => setWaypoints(prev => prev.filter((_, i) => i !== idx))}
                    title="הסר נקודה"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ═══ Right: Form panel ══════════════════════════════════════════════ */}
        <div className="mc-form-panel panel">
          <h2>פרטי המשימה</h2>

          <label>
            שם משימה <span className="req">*</span>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='לדוגמה: סיור בוקר – שדה צפוני'
              required
            />
          </label>

          <label>
            רחפן <span className="req">*</span>
            <select value={droneId} onChange={e => setDroneId(e.target.value)} required>
              <option value="">— בחר רחפן —</option>
              {drones.map(d => (
                <option key={d.id} value={d.id}>
                  {d.drone_id}{d.tail_number ? ` (${d.tail_number})` : ''} — {d.status}
                </option>
              ))}
            </select>
          </label>

          {selectedDrone && (
            <div className="mc-drone-chip">
              ✈ {selectedDrone.drone_id}
              <span className={`status status-${selectedDrone.status}`}>{selectedDrone.status}</span>
            </div>
          )}

          <label>
            סוג משימה
            <select value={missionType} onChange={e => setMissionType(e.target.value)}>
              {Object.entries(MISSION_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>

          {/* Zone type selector (zone mode only) */}
          {planMode === 'zone' && (
            <label>
              סוג אזור <span className="req">*</span>
              <select value={zoneType} onChange={e => setZoneType(e.target.value as MissionAction)}>
                <option value="scan">🔍 סריקה</option>
                <option value="spray">💧 ריסוס</option>
                <option value="takeoff">🛫 סיור</option>
                <option value="drop_delivery">📦 הורדת משלוח</option>
              </select>
            </label>
          )}

          {/* Security options — patrol and security mission types */}
          {(missionType === 'patrol' || missionType === 'security') && (
            <div className="mc-security-section">
              <div className="mc-section-title">🔒 אפשרויות אבטחה</div>
              <label className="mc-check-label">
                <input type="checkbox" checked={faceRecognition} onChange={e => setFaceRecognition(e.target.checked)} />
                <span>👤 זיהוי פנים</span>
              </label>
              <label className="mc-check-label">
                <input type="checkbox" checked={weaponDetection} onChange={e => setWeaponDetection(e.target.checked)} />
                <span>🔫 זיהוי נשק</span>
              </label>
              <label className="mc-check-label">
                <input type="checkbox" checked={sendAlerts} onChange={e => setSendAlerts(e.target.checked)} />
                <span>🔔 שלח התראות</span>
              </label>
            </div>
          )}

          {/* Dynamic instructions */}
          <div className="mc-instructions">
            <p>שלבים:</p>
            {planMode === 'single' ? (
              <ol>
                <li>בחר רחפן</li>
                <li>לחץ על המפה לסימון נקודת יעד</li>
                <li>לחץ ימני על הסמן לבחירת פעולה</li>
                <li>הזן שם משימה ולחץ "צור משימה"</li>
              </ol>
            ) : planMode === 'waypoints' ? (
              <ol>
                <li>בחר רחפן</li>
                <li>לחץ על המפה לכל נקודת מסלול (WP1, WP2…)</li>
                <li>לחץ ימני על נקודה לפעולה / גובה / הסרה</li>
                <li>הזן שם משימה ולחץ "צור משימה"</li>
              </ol>
            ) : (
              <ol>
                <li>בחר רחפן וסוג אזור</li>
                <li>לחץ על המפה להגדרת גבולות האזור (V1, V2…)</li>
                <li>לפחות 3 נקודות — האזור ייצבע אוטומטית</li>
                <li>לחץ ימני על נקודה להסרתה</li>
                <li>הזן שם משימה ולחץ "צור משימה"</li>
              </ol>
            )}
          </div>

          {/* Status summary */}
          <div className="mc-status-row">
            {planMode === 'single' ? (
              <>
                <span className={`mc-check ${selectedPt ? 'ok' : ''}`}>
                  {selectedPt ? '✓' : '○'} נקודת יעד
                </span>
                <span className={`mc-check ${selectedPt?.actionType ? 'ok' : ''}`}>
                  {selectedPt?.actionType ? '✓' : '○'} פעולה
                </span>
              </>
            ) : planMode === 'waypoints' ? (
              <span className={`mc-check ${waypoints.length >= 2 ? 'ok' : ''}`}>
                {waypoints.length >= 2 ? '✓' : '○'} {waypoints.length} נקודות
                {waypoints.length < 2 ? ' (נדרש לפחות 2)' : ''}
              </span>
            ) : (
              <span className={`mc-check ${waypoints.length >= 3 ? 'ok' : ''}`}>
                {waypoints.length >= 3 ? '✓' : '○'} {waypoints.length} נקודות גבול
                {waypoints.length < 3 ? ' (נדרש לפחות 3)' : ' — אזור מוגדר'}
              </span>
            )}
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/missions')}>ביטול</button>
            <button
              type="submit"
              className="btn-primary"
              disabled={
                createMutation.isPending ||
                (planMode === 'single'    && !selectedPt?.actionType) ||
                (planMode === 'waypoints' && waypoints.length < 2) ||
                (planMode === 'zone'      && waypoints.length < 3)
              }
            >
              {createMutation.isPending ? 'יוצר...' : '✓ צור משימה'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
